// WebAuthn passkey step-up. Wraps the browser credential API and the
// two ceremonies (enrollment + assertion) against the Go backend.
//
// The server speaks standard WebAuthn JSON: binary fields (challenge, user id,
// credential ids, attestation/assertion bytes) ride as base64url strings, which
// navigator.credentials needs as ArrayBuffers and hands back as ArrayBuffers.
// The transform/serialize helpers below are the whole job — and the only part
// with logic worth unit-testing, since the ceremony itself needs a real
// authenticator (manual smoke on dev per the issue).

import { api, ApiError } from './api';

// ─── base64url codec ─────────────────────────────────────────────────────────

// Returns a Uint8Array over a freshly-allocated ArrayBuffer (not ArrayBufferLike)
// so the result satisfies BufferSource for navigator.credentials.
export function base64urlToBytes(s: string): Uint8Array<ArrayBuffer> {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '=');
  const bin = atob(padded);
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function bytesToBase64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// ─── option transforms (server JSON → navigator BufferSource) ────────────────

interface ServerCreationOptions {
  publicKey: {
    challenge: string;
    user: { id: string; name: string; displayName: string };
    excludeCredentials?: Array<{ id: string; type: string; transports?: string[] }>;
    [k: string]: unknown;
  };
}

interface ServerAssertionOptions {
  publicKey: {
    challenge: string;
    allowCredentials?: Array<{ id: string; type: string; transports?: string[] }>;
    [k: string]: unknown;
  };
}

function decodeDescriptors(
  list?: Array<{ id: string; type: string; transports?: string[] }>,
): PublicKeyCredentialDescriptor[] | undefined {
  if (!list) return undefined;
  return list.map((d) => ({
    id: base64urlToBytes(d.id),
    type: d.type as PublicKeyCredentialType,
    transports: d.transports as AuthenticatorTransport[] | undefined,
  }));
}

export function toCreationOptions(server: ServerCreationOptions): CredentialCreationOptions {
  const pk = server.publicKey;
  return {
    publicKey: {
      ...(pk as unknown as PublicKeyCredentialCreationOptions),
      challenge: base64urlToBytes(pk.challenge),
      user: { ...pk.user, id: base64urlToBytes(pk.user.id) },
      excludeCredentials: decodeDescriptors(pk.excludeCredentials),
    },
  };
}

export function toAssertionOptions(server: ServerAssertionOptions): CredentialRequestOptions {
  const pk = server.publicKey;
  return {
    publicKey: {
      ...(pk as unknown as PublicKeyCredentialRequestOptions),
      challenge: base64urlToBytes(pk.challenge),
      allowCredentials: decodeDescriptors(pk.allowCredentials),
    },
  };
}

// ─── result serialization (navigator BufferSource → server JSON) ─────────────

export function serializeRegistration(cred: PublicKeyCredential): unknown {
  const resp = cred.response as AuthenticatorAttestationResponse;
  return {
    id: cred.id,
    rawId: bytesToBase64url(cred.rawId),
    type: cred.type,
    clientExtensionResults: cred.getClientExtensionResults(),
    response: {
      clientDataJSON: bytesToBase64url(resp.clientDataJSON),
      attestationObject: bytesToBase64url(resp.attestationObject),
    },
  };
}

export function serializeAssertion(cred: PublicKeyCredential): unknown {
  const resp = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id,
    rawId: bytesToBase64url(cred.rawId),
    type: cred.type,
    clientExtensionResults: cred.getClientExtensionResults(),
    response: {
      clientDataJSON: bytesToBase64url(resp.clientDataJSON),
      authenticatorData: bytesToBase64url(resp.authenticatorData),
      signature: bytesToBase64url(resp.signature),
      userHandle: resp.userHandle ? bytesToBase64url(resp.userHandle) : null,
    },
  };
}

// ─── ceremonies ──────────────────────────────────────────────────────────────

export function isPasskeySupported(): boolean {
  return typeof window !== 'undefined' && typeof window.PublicKeyCredential === 'function';
}

// enrollPasskey runs the registration ceremony: begin → create → finish.
// Throws on a server rejection (ApiError) or a user cancel (DOMException).
export async function enrollPasskey(): Promise<void> {
  const opts = await api<ServerCreationOptions>('/api/auth/webauthn/register/begin', { method: 'POST' });
  const cred = (await navigator.credentials.create(toCreationOptions(opts))) as PublicKeyCredential | null;
  if (!cred) throw new Error('passkey creation returned no credential');
  await api('/api/auth/webauthn/register/finish', { method: 'POST', body: serializeRegistration(cred) });
}

// stepUpWithPasskey runs the assertion ceremony: begin → get → finish via the
// shared POST /api/auth/step-up. On success the session holds its ~5-min window.
export async function stepUpWithPasskey(): Promise<void> {
  const opts = await api<ServerAssertionOptions>('/api/auth/webauthn/stepup/begin', { method: 'POST' });
  const cred = (await navigator.credentials.get(toAssertionOptions(opts))) as PublicKeyCredential | null;
  if (!cred) throw new Error('passkey assertion returned no credential');
  await api('/api/auth/step-up', { method: 'POST', body: { assertion: serializeAssertion(cred) } });
}

// isStepUpRequired reports whether an ApiError is the passkey-gate 401 that the
// sensitive-op middleware returns (code WEBAUTHN_STEP_UP_REQUIRED).
export function isStepUpRequired(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 401) return false;
  const body = err.body as { code?: string } | null;
  return body?.code === 'WEBAUTHN_STEP_UP_REQUIRED';
}

// ─── self-management ────────────────────────────────────────────────

export interface PasskeySummary {
  credentialId: string;
  label?: string;
  deviceClass?: string;
  createdAt?: string;
  lastUsedAt?: string;
}

// listPasskeys returns the caller's enrolled passkeys (summaries only). The safe
// GET also seeds the CSRF token for the DELETE that may follow.
export async function listPasskeys(signal?: AbortSignal): Promise<PasskeySummary[]> {
  const resp = await api<{ credentials: PasskeySummary[] }>('/api/auth/webauthn/credentials', { signal });
  return resp.credentials ?? [];
}

// deletePasskey removes one of the caller's passkeys by its base64url id.
export async function deletePasskey(credentialId: string): Promise<void> {
  await api(`/api/auth/webauthn/credentials/${encodeURIComponent(credentialId)}`, { method: 'DELETE' });
}
